/**
 * 相机控制（自研，轻量）：position + target 模型。
 * - 单指拖动：水平/俯仰旋转（始终看向地平线）
 * - 双指捏合 / 滚轮：缩放海拔（地面到太空）
 * - 触控优先（Pointer Events 统一处理）
 */

import * as THREE from 'three'
import { PLANET_RADIUS_KM } from '../utils/atmosphereParams'

const PITCH_MIN = -1.15 // ~ -66°，可下看地面
const PITCH_MAX = 1.55 // ~ 89°
const ALT_MIN = 0.002 // 2m
const ALT_MAX = 600 // km，太空

export interface RigState {
  yaw: number
  pitch: number
  altitudeKm: number
}

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera
  yaw = 0
  pitch = 0.5
  altitudeKm = 0.002

  private dom: HTMLElement
  private pointers = new Map<number, { x: number; y: number }>()
  private pinchDist = 0
  private disposed = false

  constructor(camera: THREE.PerspectiveCamera, dom: HTMLElement) {
    this.camera = camera
    this.dom = dom
    // 远平面需覆盖整个地球（半径 6371km）到相机（最高 600km+）的距离，
    // 否则太空视角下地球被远平面裁切
    camera.near = 1
    camera.far = 30000
    this.bindEvents()
  }

  get state(): RigState {
    return { yaw: this.yaw, pitch: this.pitch, altitudeKm: this.altitudeKm }
  }

  setState(s: Partial<RigState>): void {
    if (s.yaw !== undefined) this.yaw = s.yaw
    if (s.pitch !== undefined) this.pitch = s.pitch
    if (s.altitudeKm !== undefined) this.altitudeKm = s.altitudeKm
    this.clamp()
  }

  private bindEvents(): void {
    this.dom.addEventListener('pointerdown', this.onPointerDown)
    this.dom.addEventListener('pointermove', this.onPointerMove)
    this.dom.addEventListener('pointerup', this.onPointerUp)
    this.dom.addEventListener('pointercancel', this.onPointerUp)
    this.dom.addEventListener('wheel', this.onWheel, { passive: false })
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.dom.removeEventListener('pointerdown', this.onPointerDown)
    this.dom.removeEventListener('pointermove', this.onPointerMove)
    this.dom.removeEventListener('pointerup', this.onPointerUp)
    this.dom.removeEventListener('pointercancel', this.onPointerUp)
    this.dom.removeEventListener('wheel', this.onWheel)
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.dom.setPointerCapture(e.pointerId)
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()]
      this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y)
    }
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return
    const prev = this.pointers.get(e.pointerId)!
    const dx = e.clientX - prev.x
    const dy = e.clientY - prev.y
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (this.pointers.size === 1) {
      // 单指：旋转
      this.yaw -= dx * 0.005
      this.pitch += dy * 0.005
      this.clamp()
    } else if (this.pointers.size === 2) {
      // 双指：捏合缩放海拔
      const [a, b] = [...this.pointers.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      if (this.pinchDist > 0) {
        this.altitudeKm *= this.pinchDist / Math.max(dist, 1)
        this.clamp()
      }
      this.pinchDist = dist
    }
  }

  private onPointerUp = (e: PointerEvent): void => {
    this.pointers.delete(e.pointerId)
    if (this.pointers.size < 2) this.pinchDist = 0
  }

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault()
    this.altitudeKm *= Math.exp(e.deltaY * 0.0012)
    this.clamp()
  }

  private clamp(): void {
    this.altitudeKm = Math.min(ALT_MAX, Math.max(ALT_MIN, this.altitudeKm))
    this.pitch = Math.min(PITCH_MAX, Math.max(PITCH_MIN, this.pitch))
  }

  /** 每帧调用：根据 yaw/pitch/altitude 更新相机位置与朝向 */
  update(): void {
    const R = PLANET_RADIUS_KM
    const r = R + this.altitudeKm

    // 极点相机模型：相机位于北极上方，天顶 = +Z（与太阳仰角坐标系一致）
    // yaw 绕 +Z 旋转视线（看向不同方位），pitch 上下俯仰
    this.camera.position.set(0, 0, r)

    // 地平线切线方向（+Z 平面的切向，yaw=0 时指向 +X，即太阳方位角 0° 的方向）
    const groundForward = new THREE.Vector3(
      Math.cos(this.yaw),
      Math.sin(this.yaw),
      0,
    )
    // 前向：地平线基础上按 pitch 上仰（正=看天）
    const forward = groundForward
      .multiplyScalar(Math.cos(this.pitch))
      .add(new THREE.Vector3(0, 0, 1).multiplyScalar(Math.sin(this.pitch)))
      .normalize()

    // camera.up = 径向（+Z）→ 地平线水平（上天空、下地面）
    this.camera.up.set(0, 0, 1)
    this.camera.lookAt(this.camera.position.clone().add(forward))
  }

  /** 垂直视场角（度） */
  setFov(fovDeg: number): void {
    this.camera.fov = fovDeg
    this.camera.updateProjectionMatrix()
  }
}
